import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { buildApiUrl } from "../config/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const isLoggedIn = Boolean(user);

  const clearError = useCallback(() => {
    setAuthError("");
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/me"), {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUser(data.user);
      } else {
        setUser(null);
      }

      return data;
    } catch (error) {
      console.error("Fetch current user error:", error);
      setUser(null);
      return {
        success: false,
        message: "Could not check login status",
      };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const register = useCallback(async (formData) => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/register"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setAuthError(data.message || "Could not create account");
        return data;
      }

      setUser(data.user);

      return data;
    } catch (error) {
      console.error("Register error:", error);

      const errorData = {
        success: false,
        message: "Could not create account. Please try again.",
      };

      setAuthError(errorData.message);
      return errorData;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const login = useCallback(async ({ email, password }) => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/login"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setAuthError(data.message || "Could not login");
        return data;
      }

      setUser(data.user);

      return data;
    } catch (error) {
      console.error("Login error:", error);

      const errorData = {
        success: false,
        message: "Could not login. Please try again.",
      };

      setAuthError(errorData.message);
      return errorData;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/logout"), {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      setUser(null);

      return data;
    } catch (error) {
      console.error("Logout error:", error);

      setUser(null);

      return {
        success: false,
        message: "Logged out locally, but server logout failed",
      };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const updateAccount = useCallback(async (formData) => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/me"), {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setAuthError(data.message || "Could not update account");
        return data;
      }

      setUser(data.user);

      return data;
    } catch (error) {
      console.error("Update account error:", error);

      const errorData = {
        success: false,
        message: "Could not update account. Please try again.",
      };

      setAuthError(errorData.message);
      return errorData;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const updateMarketingPreference = useCallback(async (marketingOptIn) => {
    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(buildApiUrl("/auth/marketing"), {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ marketingOptIn }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setAuthError(data.message || "Could not update marketing preference");
        return data;
      }

      setUser((currentUser) =>
        currentUser
          ? {
              ...currentUser,
              marketingOptIn,
            }
          : currentUser
      );

      return data;
    } catch (error) {
      console.error("Marketing preference error:", error);

      const errorData = {
        success: false,
        message: "Could not update marketing preference. Please try again.",
      };

      setAuthError(errorData.message);
      return errorData;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      isLoggedIn,
      authLoading,
      authError,
      clearError,
      fetchCurrentUser,
      register,
      login,
      logout,
      updateAccount,
      updateMarketingPreference,
    }),
    [
      user,
      isLoggedIn,
      authLoading,
      authError,
      clearError,
      fetchCurrentUser,
      register,
      login,
      logout,
      updateAccount,
      updateMarketingPreference,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
